import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Col,
  Container,
  Row,
  OverlayTrigger,
  Tooltip,
  Offcanvas,
  Accordion,
  Badge,
  Spinner,
} from "react-bootstrap";
import { jwtDecode } from "jwt-decode";
import { getAccountsByPracticeNumbers, getUserPracticeById } from "../../api";
import AccountSearch from "../SearchAccounts/SearchAccounts";
import { useNavigate } from "react-router-dom";
import CreateReceiptModal from "./Receipt";
import CreditNote from "./CreditNote";
import "./AccountList.css";

export default function AccountList() {
  const [accounts, setAccounts] = useState([]);
  const [sortedAccounts, setSortedAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [showCreditNoteModal, setShowCreditNoteModal] = useState(false);

  // pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const totalPages = Math.ceil(total / limit) || 1;

  // sorting
  const [sortConfig, setSortConfig] = useState({
    key: "accountOutstandingAmount",
    direction: "desc",
  });

  // data for filters
  const [practice, setPractice] = useState([]);

  // committed filters (drive the table)
  const [selectedPractices, setSelectedPractices] = useState([]);
  const [selectedAccountTypes, setSelectedAccountTypes] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [selectedAccountData, setSelectedAccountData] = useState(null); // Store the searched account data

  // temp filters (edited inside panel)
  const [tmpPractices, setTmpPractices] = useState([]);
  const [tmpAccountTypes, setTmpAccountTypes] = useState([]);

  // UI
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [userRole, setUserRole] = useState(false);

  const navigate = useNavigate();

  // fetch accounts + practices
  useEffect(() => {
    async function getData() {
      try {
        // Use different loading states for initial load vs pagination
        if (accounts.length === 0) {
          setLoading(true);
        } else {
          setPageLoading(true);
        }

        const token = sessionStorage.getItem("user");
        if (!token) throw new Error("No user token found");
        const user = jwtDecode(token);
         const roles = Array.isArray(user?.role) ? user.role : [];
      // Check if roles contain at least one of the allowed roles
      const hasAllowedRole = roles.includes("SuperAdmin") || roles.includes("Finance");

      if (!hasAllowedRole) {
          setUserRole(false);
        } else {
          setUserRole(true);
        }

        const res = await getAccountsByPracticeNumbers({
          practiceNumbers: user.practice,
          page,
          limit,
        });

        if (res && Array.isArray(res.accounts)) {
          setAccounts(res.accounts);
          setTotal(res.total || res.accounts.length);
        } else {
          setAccounts([]);
          setTotal(0);
        }

        // Only fetch practices on initial load
        if (practice.length === 0) {
          const fetchedPractices = await getUserPracticeById(user._id);
          setPractice(Array.isArray(fetchedPractices) ? fetchedPractices : []);
        }
      } catch (err) {
        console.error("Error loading accounts:", err);
        setAccounts([]);
        if (practice.length === 0) {
          setPractice([]);
        }
        setTotal(0);
      } finally {
        setLoading(false);
        setPageLoading(false);
      }
    }
    getData();
  }, [page, limit]);

  // sorting
  useEffect(() => {
    if (Array.isArray(accounts)) {
      sortData(sortConfig.key, sortConfig.direction, accounts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts]);

  const sortData = (key, direction, customList = accounts) => {
    if (!Array.isArray(customList) || customList.length === 0) {
      setSortedAccounts([]);
      return;
    }
    const sorted = [...customList].sort((a, b) => {
      let valA = a[key] ?? "";
      let valB = b[key] ?? "";
      if (key === "accountOutstandingAmount") {
        valA = Number(valA) || 0;
        valB = Number(valB) || 0;
        return direction === "asc" ? valA - valB : valB - valA;
      } else {
        valA = String(valA).toLowerCase();
        valB = String(valB).toLowerCase();
        if (valA === valB) return 0;
        return direction === "asc"
          ? valA < valB
            ? -1
            : 1
          : valA > valB
          ? -1
          : 1;
      }
    });
    setSortedAccounts(sorted);
    setSortConfig({ key, direction });
  };

  const toggleSort = (key) => {
    const newDirection =
      sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    sortData(key, newDirection);
  };

  // search select - MODIFIED TO STORE ACCOUNT DATA
  const handleAccountSearchSelect = (account) => {
    if (account && account._id) {
      setSelectedAccountId(account._id);
      setSelectedAccountData(account); // Store the full account data
    } else {
      setSelectedAccountId(null);
      setSelectedAccountData(null);
    }
  };

  // filtering effect (from committed filters + search) - MODIFIED
  useEffect(() => {
    let filtered = [...accounts];

    // practices
    if (selectedPractices.length > 0) {
      filtered = filtered.filter((acc) => {
        const accountPractice = acc.accountPractice;
        if (Array.isArray(accountPractice)) {
          return accountPractice.some((p) => selectedPractices.includes(p));
        }
        return selectedPractices.includes(accountPractice);
      });
    }

    // account type
    if (selectedAccountTypes.length > 0) {
      filtered = filtered.filter((acc) => {
        const isPrivate =
          !acc.accountMedicalAid || acc.accountMedicalAid === "";
        const type = isPrivate ? "Private" : "Medical Aid";
        return selectedAccountTypes.includes(type);
      });
    }

    // specific account (search) - MODIFIED LOGIC
    if (selectedAccountId && selectedAccountData) {
      // Check if the searched account is in the current filtered results
      const foundInFiltered = filtered.find((acc) => acc._id === selectedAccountId);
      
      if (foundInFiltered) {
        // Account is in current page, filter normally
        filtered = [foundInFiltered];
      } else {
        // Account is not in current page, show only the searched account
        filtered = [selectedAccountData];
      }
    }

    sortData(sortConfig.key, sortConfig.direction, filtered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPractices, selectedAccountTypes, selectedAccountId, selectedAccountData, accounts]);

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key)
      return <i className="fa-solid fa-arrows-up-down text-primary ms-2" />;
    return sortConfig.direction === "asc" ? (
      <i className="fa-solid fa-arrow-up text-primary ms-2" />
    ) : (
      <i className="fa-solid fa-arrow-down text-primary ms-2" />
    );
  };

  // panel helpers
  const toggleIn = (arr, val) =>
    arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val];

  const openFilters = () => {
    setTmpPractices(selectedPractices);
    setTmpAccountTypes(selectedAccountTypes);
    setShowFilters(true);
  };

  const applyFilters = () => {
    setSelectedPractices(tmpPractices);
    setSelectedAccountTypes(tmpAccountTypes);
    setShowFilters(false);
    setPage(1);
  };

  const clearFilters = () => {
    setSelectedPractices([]);
    setSelectedAccountTypes([]);
    setSelectedAccountId(null);
    setSelectedAccountData(null); // Clear the stored account data
    setPage(1);
  };

  // MODIFIED: Clear search function
  const clearSearch = () => {
    setSelectedAccountId(null);
    setSelectedAccountData(null);
  };

  const activeCount =
    (selectedPractices.length ? 1 : 0) +
    (selectedAccountTypes.length ? 1 : 0) +
    (selectedAccountId ? 1 : 0);

  return (
    <>
      {/* Top Bar: Search + Filter + Clear + Pagination */}
      <Container className="mb-2">
        <Row className="align-items-center g-2">
          <Col xs={12} md={6}>
            <Row className="align-items-center g-2">
              <div className="d-flex align-items-start gap-2 mb-2 mb-md-0">
                <Col md={11} xs={12}>
                  <AccountSearch onAccountSelect={handleAccountSearchSelect} />
                </Col>
                <Col md={1} xs={12} className="d-flex justify-content-end">
                  {selectedAccountId && (
                    <Button
                      variant="outline-secondary"
                      onClick={clearSearch} // Use the new clearSearch function
                    >
                      <i className="fa-solid fa-rotate-right"></i>
                    </Button>
                  )}
                </Col>
              </div>
            </Row>
          </Col>

          <Col
            xs={12}
            md
            className="d-flex justify-content-md-end gap-2 mt-2 mt-md-0"
          >
            {!selectedAccountId && (
              <>
                <Button
                  variant="primary"
                  onClick={openFilters}
                  style={{ width: "120px" }}
                >
                  <i className="fa-solid fa-filter me-2" />
                  Filter{" "}
                  {activeCount > 0 && (
                    <Badge bg="light" text="dark" className="ms-2">
                      {activeCount}
                    </Badge>
                  )}
                </Button>
                <Button
                  variant="outline-danger"
                  onClick={clearFilters}
                  disabled={!activeCount}
                  style={{ width: "120px" }}
                >
                  <i className="fa-solid fa-trash me-2" />
                  Filters
                </Button>
                <Button
                  disabled={page === 1 || pageLoading}
                  onClick={() => setPage((p) => p - 1)}
                  className="ms-2"
                  variant="primary"
                >
                  {pageLoading ? (
                    <Spinner size="sm" />
                  ) : (
                    <i className="fa-solid fa-angle-left" />
                  )}
                </Button>
                <div className="text-nowrap small d-flex align-items-center">
                  <span className="mx-2">
                    Page {page} of {totalPages}
                  </span>
                </div>
                <Button
                  disabled={page === totalPages || pageLoading}
                  onClick={() => setPage((p) => p + 1)}
                  variant="primary"
                >
                  {pageLoading ? (
                    <Spinner size="sm" />
                  ) : (
                    <i className="fa-solid fa-angle-right" />
                  )}
                </Button>
              </>
            )}
          </Col>
        </Row>
      </Container>

      {/* Table Container with Loading Overlay */}
      <Container style={{ position: "relative" }}>
        {/* Loading Overlay for Page Transitions */}
        {pageLoading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 15,
              right: 15,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
              borderRadius: "5px",
            }}
          >
            <div className="text-center">
              <Spinner animation="border" variant="primary" />
              <div className="mt-2 text-primary">Loading page {page}...</div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Loading accounts...</p>
          </div>
        ) : sortedAccounts.length === 0 ? (
          <p>No accounts found.</p>
        ) : (
          <Table
            bordered
            hover
            responsive
            style={{
              opacity: pageLoading ? 0.5 : 1,
              transition: "opacity 0.2s ease-in-out",
            }}
          >
            <thead className="table-primary text-center fixed-table">
              <tr>
                <th
                  className="text-center align-middle text-primary"
                  style={{ width: "2%" }}
                >
                  #
                </th>
                <th
                  className="text-center align-middle text-primary"
                  style={{ width: "15%" }}
                >
                  Account Number
                </th>
                <th
                  className="text-center align-middle"
                  style={{ width: "18%" }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-primary">Last Name, Full Name</span>
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 ms-2"
                      onClick={() => toggleSort("accountLastName")}
                    >
                      {renderSortIcon("accountLastName")}
                    </Button>
                  </div>
                </th>
                <th
                  className="text-center align-middle text-primary"
                  style={{ width: "15%" }}
                >
                  ID Number
                </th>
                <th
                  className="text-center align-middle text-primary"
                  style={{ width: "10%" }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-primary">Medical Aid</span>
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 ms-2"
                      onClick={() => toggleSort("accountMedicalAid")}
                    >
                      {renderSortIcon("accountMedicalAid")}
                    </Button>
                  </div>
                </th>
                <th
                  className="text-center align-middle text-primary"
                  style={{ width: "15%" }}
                >
                  Plan | Option
                </th>
                <th
                  className="text-center align-middle text-primary"
                  style={{ width: "10%" }}
                >
                  Medical Aid Number
                </th>
                <th
                  className="text-center align-middle text-primary"
                  style={{ width: "5%" }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <span>Outstanding Balance</span>
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 ms-2"
                      onClick={() => toggleSort("accountOutstandingAmount")}
                    >
                      {renderSortIcon("accountOutstandingAmount")}
                    </Button>
                  </div>
                </th>
                {userRole && (
                <th
                  className="text-center align-middle text-primary"
                  style={{ width: "10%" }}
                >
                  Actions
                </th>
                )}
              </tr>
            </thead>

            <tbody>
              {sortedAccounts.map((account, idx) => (
                <tr
                  key={account._id}
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate(`/accounts/${account._id}`)}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#6c757d")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "")
                  }
                >
                  <td>{selectedAccountId ? 1 : (page - 1) * limit + idx + 1}</td>
                  <td>{account.accountNumber}</td>
                  <td>
                    {account.accountLastName}, {account.accountFullNames}
                  </td>
                  <td>{account.accountIdNumber}</td>
                  <td>
                    {account.accountMedicalAid
                      ? account.accountMedicalAid
                      : "Private"}
                  </td>
                  <td>
                    {account.accountMedicalAidPlan}
                    {account.accountMedicalAidOption
                      ? ` | ${account.accountMedicalAidOption}`
                      : ""}
                  </td>
                  <td>{account.accountMedicalAidNumber}</td>
                  <td className="text-end">
                    {account.accountOutstandingAmount
                      ? `R${Number(account.accountOutstandingAmount).toFixed(
                          2
                        )}`
                      : "R0.00"}
                  </td>
                  {userRole && (
                  <td className="text-center">
                    <div className="d-flex justify-content-center gap-1 size-4">
                      {[
                        {
                          icon: "fa-file-invoice-dollar",
                          label: "Invoice",
                          onClick: (e) => {
                            e.stopPropagation();
                            navigate(`/invoices/1/${account._id}`);
                          },
                        },
                        {
                          icon: "fa-receipt",
                          label: "Receipt",
                          onClick: (e) => {
                            e.stopPropagation();
                            setSelectedAccount(account);
                            setShowReceiptModal(true);
                          },
                        },
                         {
                          icon: "fa fa-undo",
                          label: "Credit Note",
                          onClick: (e) => {
                            e.stopPropagation();
                            setSelectedAccount(account);
                            setShowCreditNoteModal(true);
                          },
                        },
                      ].map((btn, i) => (
                        <OverlayTrigger
                          key={i}
                          placement="top"
                          overlay={
                            <Tooltip id={`tooltip-${btn.icon}`}>
                              {btn.label}
                            </Tooltip>
                          }
                        >
                          <Button
                            variant="link"
                            className="text-primary p-1"
                            onClick={(e) => btn.onClick(e)}
                          >
                            <i className={`fa-solid ${btn.icon} fs-6`} />
                          </Button>
                        </OverlayTrigger>
                      ))}
                    </div>
                  </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Container>

      {/* Filter Offcanvas */}
      <Offcanvas
        show={showFilters}
        onHide={() => setShowFilters(false)}
        placement="start"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title className="text-primary d-flex align-items-center">
            <i className="fa-solid fa-filter me-2 text-primary" />
            Filters
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Accordion alwaysOpen>
            {/* Practices */}
            <Accordion.Item eventKey="0">
              <Accordion.Header>Practices</Accordion.Header>
              <Accordion.Body>
                <div className="mb-2 d-flex gap-2">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() =>
                      setTmpPractices(
                        practice.map((p) => p.practiceNumber).filter(Boolean)
                      )
                    }
                  >
                    Select all
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setTmpPractices([])}
                  >
                    Clear
                  </Button>
                </div>
                <div
                  className="d-flex flex-column"
                  style={{ maxHeight: 260, overflowY: "auto" }}
                >
                  {practice.length > 0 ? (
                    practice.map((p) => (
                      <div key={p.practiceNumber} className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input text-primary"
                          id={`practice-${p.practiceNumber}`}
                          checked={tmpPractices.includes(p.practiceNumber)}
                          onChange={() =>
                            setTmpPractices((arr) =>
                              toggleIn(arr, p.practiceNumber)
                            )
                          }
                        />
                        <label
                          className="form-check-label text-primary"
                          htmlFor={`practice-${p.practiceNumber}`}
                        >
                          {p.practiceName}
                        </label>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted mb-0">Loading practices...</p>
                  )}
                </div>
              </Accordion.Body>
            </Accordion.Item>

            {/* Account Type */}
            <Accordion.Item eventKey="1">
              <Accordion.Header>Account Type</Accordion.Header>
              <Accordion.Body>
                {["Medical Aid", "Private"].map((type) => (
                  <div key={type} className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input text-primary"
                      id={`type-${type}`}
                      checked={tmpAccountTypes.includes(type)}
                      onChange={() =>
                        setTmpAccountTypes((arr) => toggleIn(arr, type))
                      }
                    />
                    <label
                      className="form-check-label text-primary"
                      htmlFor={`type-${type}`}
                    >
                      {type}
                    </label>
                  </div>
                ))}
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>

          <div className="mt-4 d-flex gap-2">
            <Button onClick={applyFilters} style={{ width: "120px" }}>
              <i className="fa-solid fa-filter me-2 text-white" />
              Apply
            </Button>
            <Button
              variant="outline-danger"
              style={{ width: "120px" }}
              onClick={() => {
                setTmpPractices([]);
                setTmpAccountTypes([]);
              }}
            >
              <i className="fa-solid fa-trash me-2" />
              Filters
            </Button>
          </div>
          <div className="mt-2 text-muted small">
            Tip: leaving a section empty means "All".
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Receipt Modal */}
      {showReceiptModal && selectedAccount && (
        <CreateReceiptModal
          show={showReceiptModal}
          onHide={() => setShowReceiptModal(false)}
          account={selectedAccount}
        />
      )}
      {showCreditNoteModal && selectedAccount && (
      <CreditNote
                show={showCreditNoteModal}
                onHide={() => setShowCreditNoteModal(false)}
                account={selectedAccount}
              />
      )}
    </>
  );
}